import React from 'react';
import {
    ChevronUp,
    ChevronDown,
    ChevronsLeft,
    ChevronsRight,
    ChevronLeft,
    ChevronRight,
} from 'lucide-react';
import { Skeleton } from 'components/ui/skeleton';
import { useSmoothLoading } from 'lib/use-smooth-loading';
import type { CNHoldRow } from '../api';

type SortDirection = 'asc' | 'desc';

type ColumnDef = {
    key: keyof CNHoldRow | 'stock_days';
    label: string;
    sortable?: boolean;
};

interface Props {
    data: CNHoldRow[];
    loading: boolean;
    page: number;
    pageSize: number;
    totalItems: number;
    sortBy: keyof CNHoldRow;
    sortOrder: SortDirection;
    onSortChange: (column: keyof CNHoldRow) => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
}

const columns: ColumnDef[] = [
    { key: 'cn_number', label: 'CN Number' },
    { key: 'cn_date', label: 'CN Date' },
    { key: 'booking_branch', label: 'Booking Branch' },
    { key: 'consignor', label: 'Consignor' },
    { key: 'consignee', label: 'Consignee' },
    { key: 'origin', label: 'Origin' },
    { key: 'destination', label: 'Destination' },
    { key: 'stock_location', label: 'Stock Location' },
    { key: 'stock_since', label: 'Stock Since' },
    { key: 'stock_days', label: 'Stock Days', sortable: false },
    { key: 'hold_by_user', label: 'Hold By User' },
    { key: 'hold_reason', label: 'Hold Reason' },
    { key: 'remark', label: 'Remark' },
];

const CNHoldTable: React.FC<Props> = ({data, loading, page, pageSize, totalItems, sortBy, sortOrder, onSortChange, onPageChange, onPageSizeChange}) => {
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

    const { isLoading } = useSmoothLoading(loading, {
        minLoadingTime: 150,
        debounceTime: 150,
    });

    const formatValue = (value: CNHoldRow[keyof CNHoldRow]) => {
        if (value === null || value === undefined || value === '') {
            return '-';
        }

        if (typeof value === 'string') {
            if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                const parsed = new Date(value);

                if (!Number.isNaN(parsed.getTime())) {
                    return parsed.toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                    });
                }
            }

            if (value.includes('T')) {
                const parsed = new Date(value);

                if (!Number.isNaN(parsed.getTime())) {
                    return parsed.toLocaleString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                    });
                }
            }
            return value;
        }
        return String(value);
    };

    const getStockDays = (stockSince: string | null) => {
        if (!stockSince) return '-';

        const date = new Date(stockSince);
        if (Number.isNaN(date.getTime())) {
            return '-';
        }

        const now = new Date();
        const diffMs = now.getTime() - date.getTime();

        return Math.floor(diffMs / (1000 * 60 * 60 * 24));
    };

    const renderSkeletonRows = () =>
        Array.from({ length: 10 }).map((_, rowIndex) => (
            <tr key={`cn-hold-skeleton-${rowIndex}`}>
                {columns.map((_, colIndex) => (
                    <td
                        key={`${rowIndex}-${colIndex}`}
                        className="border border-gray-200 px-2 py-1.5"
                    >
                        <Skeleton className={`h-4 ${colIndex === 0 ? 'w-3/4' : 'w-full'}`} />
                    </td>
                ))}
            </tr>
        ));

    return (
        <div className="flex flex-1 min-h-0 flex-col">
            <div className="min-h-0 flex-1 overflow-auto border border-gray-200">
                <table className="w-full border-collapse text-sm">
                    <thead>
                        <tr className="bg-gray-100">
                            {columns.map((column) => {
                                const active = sortBy === column.key;

                                return (
                                    <th key={String(column.key)}
                                        onClick={() => {
                                            if (column.sortable !== false) {
                                                onSortChange(column.key as keyof CNHoldRow);
                                            }
                                        }}
                                        className={`border border-gray-300 px-2 py-2 whitespace-nowrap text-left font-semibold ${column.sortable === false ? '' : 'cursor-pointer'}`}
                                    >
                                        <div className="flex items-center gap-1">
                                            {column.label}

                                            {column.sortable !== false && (
                                                active ? (sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />) : (<ChevronUp size={14} className="text-gray-300" />)
                                            )}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>

                    <tbody key={`${sortBy}-${sortOrder}`}>
                        {isLoading ? (
                            renderSkeletonRows()
                        ) : data.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className="py-16 text-center text-gray-500">
                                    No data available
                                </td>
                            </tr>
                        ) : (
                            data.map((row, idx) => (
                                <tr
                                    key={`${row.cn_number}-${idx}`}
                                    className="hover:bg-gray-50"
                                >
                                    {columns.map((column) => (
                                        <td
                                            key={String(column.key)}
                                            className="border border-gray-200 px-2 py-1.5 whitespace-nowrap"
                                        >
                                            {column.key === 'stock_days' ? getStockDays(row.stock_since) : formatValue(row[column.key as keyof CNHoldRow])}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <div className="shrink-0 flex items-center justify-between pt-4">
                <span className="text-sm text-gray-600">
                    Showing{' '}
                    <strong>
                        {totalItems === 0 ? 0 : (page - 1) * pageSize + 1}
                    </strong>{' '}
                    to{' '}
                    <strong>{Math.min(page * pageSize, totalItems)}</strong> of{' '}
                    <strong>{totalItems}</strong>
                </span>

                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-sm">
                        Rows per page
                        <select
                            value={pageSize}
                            onChange={(e) =>
                                onPageSizeChange(Number(e.target.value))
                            }
                            className="border rounded px-2 py-1"
                        >
                            {[10, 20, 50, 100].map((size) => (
                                <option
                                    key={size}
                                    value={size}
                                >
                                    {size}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center gap-1">
                        <button
                            disabled={page === 1}
                            onClick={() => onPageChange(1)}
                            className="p-1 border rounded disabled:opacity-50"
                        >
                            <ChevronsLeft size={16} />
                        </button>

                        <button
                            disabled={page === 1}
                            onClick={() => onPageChange(page - 1)}
                            className="p-1 border rounded disabled:opacity-50"
                        >
                            <ChevronLeft size={16} />
                        </button>

                        <span className="px-2 text-sm">
                            Page <strong>{page}</strong> / {totalPages}
                        </span>

                        <button
                            disabled={page === totalPages}
                            onClick={() => onPageChange(page + 1)}
                            className="p-1 border rounded disabled:opacity-50"
                        >
                            <ChevronRight size={16} />
                        </button>

                        <button
                            disabled={page === totalPages}
                            onClick={() => onPageChange(totalPages)}
                            className="p-1 border rounded disabled:opacity-50"
                        >
                            <ChevronsRight size={16} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CNHoldTable;